const API_BASE = process.env.REACT_APP_API_BASE || '/api';

function getToken() {
  return localStorage.getItem('token');
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }

  if (!res.ok) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

export function login(email, password, rememberMe = false) {
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, rememberMe }),
  });
}

export function getMe() {
  return request('/auth/me');
}

export function getAuthProviders() {
  return request('/auth/providers');
}

export function updatePassword(currentPassword, newPassword) {
  return request('/auth/update-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export function validateSetupToken(token) {
  return request(`/auth/validate-setup-token?token=${encodeURIComponent(token)}`);
}

export function setPassword(token, password) {
  return request('/auth/set-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export function resetPassword(email) {
  return request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function createUser(userData) {
  return request('/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
}

export function getUsers(orgId) {
  return request(`/users?orgId=${orgId}`);
}

export function createOrganisation(orgData) {
  return request('/organisations', {
    method: 'POST',
    body: JSON.stringify(orgData),
  });
}

export function getOrganisations() {
  return request('/organisations?size=1000');
}

export function updateOrganisation(orgId, data) {
  return request(`/organisations/${orgId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function updateUser(id, data) {
  return request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function getOrganisationUsers(orgId) {
  return request(`/organisations/${orgId}/users`);
}

export function resendSetupLink(userId) {
  return request(`/users/${userId}/resend-setup`, { method: 'POST' });
}

export function removeUserFromOrganisation(orgId, userId) {
  return request(`/organisations/${orgId}/users/${userId}`, { method: 'DELETE' });
}

export function getOrganisationProfiles(orgId) {
  return request(`/organisations/${orgId}/profiles`);
}

export function lookupUserByEmail(email) {
  return request(`/users/lookup?email=${encodeURIComponent(email)}`);
}

export function addUserToOrganisation(orgId, userId, profileId) {
  return request(`/organisations/${orgId}/users`, {
    method: 'POST',
    body: JSON.stringify({ userId, profileId }),
  });
}

export function getOrganisationMembers(orgId) {
  return request(`/organisations/${orgId}/members`);
}

// --- Sites ---

export function getSites(orgId) {
  return request(`/sites/organisation/${orgId}`);
}

export function createSite(data) {
  return request('/sites', { method: 'POST', body: JSON.stringify(data) });
}

export function updateSite(id, data) {
  return request(`/sites/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function deleteSite(id) {
  return request(`/sites/${id}`, { method: 'DELETE' });
}

export function reorderSites(orgId, items) {
  return request(`/sites/organisation/${orgId}/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  });
}

// --- Locations ---

export function getLocations(orgId) {
  return request(`/locations/organisation/${orgId}`);
}

export function getLocationTree(orgId) {
  return request(`/locations/organisation/${orgId}/tree`);
}

export function getLocationsBySite(siteId) {
  return request(`/locations/site/${siteId}`);
}

export function getLocationTreeBySite(siteId) {
  return request(`/locations/site/${siteId}/tree`);
}

export function createLocation(data) {
  return request('/locations', { method: 'POST', body: JSON.stringify(data) });
}

export function updateLocation(id, data) {
  return request(`/locations/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function deleteLocation(id) {
  return request(`/locations/${id}`, { method: 'DELETE' });
}

export function reorderLocations(orgId, items) {
  return request(`/locations/organisation/${orgId}/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  });
}

export function reorderLocationsBySite(siteId, items) {
  return request(`/locations/site/${siteId}/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  });
}

// --- Reports ---

export function createReport(data) {
  return request('/reports', { method: 'POST', body: JSON.stringify(data) });
}

export async function getReportsByOrg(orgId, { page = 0, size = 100, sortBy = 'assessmentDate', sortDir = 'desc' } = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const params = new URLSearchParams({ page, size, sortBy, sortDir });
  const res = await fetch(`${API_BASE}/reports/organisation/${orgId}?${params}`, { headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);

  return {
    data,
    totalCount: parseInt(res.headers.get('X-Total-Count') || '0', 10),
    totalPages: parseInt(res.headers.get('X-Total-Pages') || '0', 10),
  };
}

export async function exportReportListExcel(orgId, { sortBy = 'assessmentDate', sortDir = 'desc' } = {}) {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const params = new URLSearchParams({ sortBy, sortDir });
  const res = await fetch(`${API_BASE}/reports/organisation/${orgId}/export-excel?${params}`, { headers });
  if (!res.ok) throw new Error('Failed to export report list');

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'reports.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function getReport(id) {
  return request(`/reports/${id}`);
}

export function duplicateReport(id) {
  return request(`/reports/${id}/duplicate`, { method: 'POST' });
}

export function deleteReport(id) {
  return request(`/reports/${id}`, { method: 'DELETE' });
}

export function updateReport(id, data) {
  return request(`/reports/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function updateReportStatus(id, status) {
  return request(`/reports/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
}

export function saveNarrative(reportId, data) {
  return request(`/reports/${reportId}/narratives`, { method: 'POST', body: JSON.stringify(data) });
}

export function saveAnswers(reportId, data) {
  return request(`/reports/${reportId}/answers`, { method: 'POST', body: JSON.stringify(data) });
}

export function saveRecommendation(reportId, data) {
  return request(`/reports/${reportId}/recommendations`, { method: 'POST', body: JSON.stringify(data) });
}

export function deleteRecommendation(id) {
  return request(`/reports/recommendations/${id}`, { method: 'DELETE' });
}

export function saveContact(reportId, data) {
  return request(`/reports/${reportId}/contacts`, { method: 'POST', body: JSON.stringify(data) });
}

export function deleteContact(id) {
  return request(`/reports/contacts/${id}`, { method: 'DELETE' });
}

export function savePlantItem(reportId, data) {
  return request(`/reports/${reportId}/plant-items`, { method: 'POST', body: JSON.stringify(data) });
}

export function deletePlantItem(id) {
  return request(`/reports/plant-items/${id}`, { method: 'DELETE' });
}

export function saveAssetEntry(reportId, data) {
  return request(`/reports/${reportId}/asset-register`, { method: 'POST', body: JSON.stringify(data) });
}

export function deleteAssetEntry(id) {
  return request(`/reports/asset-register/${id}`, { method: 'DELETE' });
}

export function saveMonitoringTask(reportId, data) {
  return request(`/reports/${reportId}/monitoring-tasks`, { method: 'POST', body: JSON.stringify(data) });
}

export function deleteMonitoringTask(id) {
  return request(`/reports/monitoring-tasks/${id}`, { method: 'DELETE' });
}

// --- Site Image ---

export async function uploadSiteImage(reportId, file) {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${API_BASE}/reports/${reportId}/site-image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) throw new Error('Failed to upload image');
  return res.json();
}

export function getSiteImageUrl(reportId) {
  return `${API_BASE}/reports/${reportId}/site-image`;
}

export async function deleteSiteImage(reportId) {
  return request(`/reports/${reportId}/site-image`, { method: 'DELETE' });
}

export function getReportSections() {
  return request('/reports/sections');
}

export function getSectionQuestions(sectionId) {
  return request(`/reports/sections/${sectionId}/questions`);
}

export function getRiskRatings() {
  return request('/reports/risk-ratings');
}

export function getScopeOfAssessmentTypes() {
  return request('/reports/scope-of-assessment-types');
}

// --- Training Records ---

export function getMyTrainingRecords() {
  return request('/training-records/my');
}

export function getUserTrainingRecords(userId) {
  return request(`/training-records/user/${userId}`);
}

export function createTrainingRecord(data) {
  return request('/training-records', { method: 'POST', body: JSON.stringify(data) });
}

export function createTrainingRecordForUser(userId, data) {
  return request(`/training-records/user/${userId}`, { method: 'POST', body: JSON.stringify(data) });
}

export function updateTrainingRecord(id, data) {
  return request(`/training-records/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function deleteTrainingRecord(id) {
  return request(`/training-records/${id}`, { method: 'DELETE' });
}

export async function uploadTrainingRecordFile(recordId, file) {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${API_BASE}/training-records/${recordId}/file`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) throw new Error('Failed to upload file');
  return res.json();
}

export function getTrainingRecordFileUrl(recordId) {
  return `${API_BASE}/training-records/${recordId}/file`;
}

export function deleteTrainingRecordFile(recordId) {
  return request(`/training-records/${recordId}/file`, { method: 'DELETE' });
}

// --- Org Documents (Certifications & Insurance) ---

export function getOrgDocuments(orgId) {
  return request(`/org-documents/organisation/${orgId}`);
}

export function createOrgDocument(data) {
  return request('/org-documents', { method: 'POST', body: JSON.stringify(data) });
}

export function updateOrgDocument(id, data) {
  return request(`/org-documents/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export function deleteOrgDocument(id) {
  return request(`/org-documents/${id}`, { method: 'DELETE' });
}

export async function uploadOrgDocumentFile(id, file) {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${API_BASE}/org-documents/${id}/file`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) throw new Error('Failed to upload file');
  return res.json();
}

export function getOrgDocumentFileUrl(id) {
  return `${API_BASE}/org-documents/${id}/file`;
}

export function deleteOrgDocumentFile(id) {
  return request(`/org-documents/${id}/file`, { method: 'DELETE' });
}

// --- Licences ---

export function createLicence(data) {
  return request('/licences', { method: 'POST', body: JSON.stringify(data) });
}

export function getAllLicences() {
  return request('/licences');
}

export function redeemLicence(data) {
  return request('/licences/redeem', { method: 'POST', body: JSON.stringify(data) });
}

export function getOrgLicenceSummary(orgId) {
  return request(`/licences/organisation/${orgId}`);
}

export function getReportExportLogs(reportId) {
  return request(`/reports/${reportId}/export-logs`);
}

// --- Features / Permissions ---

export function getFeatures() {
  return request('/features');
}

export function getProfileFeatures(orgId, profileId) {
  return request(`/organisations/${orgId}/profiles/${profileId}/features`);
}

export function updateProfileFeatures(orgId, profileId, features) {
  return request(`/organisations/${orgId}/profiles/${profileId}/features`, {
    method: 'PUT',
    body: JSON.stringify({ features }),
  });
}

export async function downloadDataReport(id) {
  const token = getToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/reports/${id}/export-data`, { headers });
  if (!res.ok) {
    throw new Error('Failed to generate data report');
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition');
  let filename = 'data_report.docx';
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match) filename = match[1];
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadReport(id) {
  const token = getToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/reports/${id}/export`, { headers });
  if (!res.ok) {
    throw new Error('Failed to generate report');
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition');
  let filename = 'report.docx';
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match) filename = match[1];
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
